import { css } from '@emotion/react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, Fab } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Ref, useLayoutEffect } from 'react';
import { Panel, PanelImperativeHandle, Separator, usePanelRef } from 'react-resizable-panels';
import { Breakpoint } from '../../constants/styles';
import ChatBot from '../../pages/ChatBot/ChatBot';
import { isNavigationOpenAtom } from '../Layout/state/NavigationState';
import { useChatBotPanelStateSync } from './chatbot-panel-hooks';
import { ShowChatBotAtom } from './chatbot-state';

export { ShowChatBotAtom };

export const ChatBotDrawerDefaultWidth = 500;
const ChatBotDrawerStyles = css`
  border-left: var(--mui-palette-primary-light) 1px solid;
  width: 100%;
  position: relative;
  min-height: 100dvh;
  max-height: 100dvh;
  overflow: hidden;
  background: var(--mui-palette-background-default);
`;

export function ChatBotButton() {
  const [showChatBot, setShowChatBot] = useAtom(ShowChatBotAtom);

  if (showChatBot) {
    // The panel's own header close button takes over while open; an empty cell
    // keeps the app-header grid columns stable (same as the audit-view case).
    return <div />;
  }

  return (
    <Fab
      aria-label='open foresight assistant'
      color='primary'
      size='small'
      title='foresight assistant'
      onClick={() => setShowChatBot(true)}
      sx={{
        zIndex: 0,
        justifySelf: 'end',
      }}
    >
      <AutoAwesomeIcon />
    </Fab>
  );
}

export function ChatBotDrawer() {
  const showChatBot = useAtomValue(ShowChatBotAtom);
  if (!showChatBot) {
    return null;
  }
  return (
    <Box css={ChatBotDrawerStyles}>
      <ChatBot />
    </Box>
  );
}

function useCloseNavbarOnChatBotOpen() {
  const setShowSideNav = useSetAtom(isNavigationOpenAtom);
  const showChatBot = useAtomValue(ShowChatBotAtom);
  useLayoutEffect(() => {
    if (showChatBot && window.innerWidth < Breakpoint.xLarge) {
      setShowSideNav(false);
    }
  }, [showChatBot, setShowSideNav]);
}

export function ChatBotSection() {
  const panelRef = usePanelRef();
  useCloseNavbarOnChatBotOpen();
  const onResize = useChatBotPanelStateSync(panelRef);

  return (
    <>
      <Separator />
      <Panel
        // Type assertion needed due to library typing mismatch between usePanelRef return type and panelRef prop type
        panelRef={panelRef as Ref<PanelImperativeHandle>}
        id='chatbot-drawer'
        collapsible
        collapsedSize={0}
        defaultSize={0}
        onResize={onResize}
        minSize={350}
      >
        <ChatBotDrawer />
      </Panel>
    </>
  );
}
